import { useState } from "react";
import { Button, Modal, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import "./ShippingAddressModal.css";

interface Address {
  id: string;
  addressId: number;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  licNumber: string;
}

interface ShippingAddressModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (address: Address) => void;
  addresses: Address[];
  selectedAddressId?: string;
}

const ShippingAddressModal: React.FC<ShippingAddressModalProps> = ({
  open,
  onClose,
  onConfirm,
  addresses,
  selectedAddressId,
}) => {
  const [selectedId, setSelectedId] = useState<string | undefined>(
    selectedAddressId,
  );

  const handleConfirm = () => {
    const selectedAddress = addresses.find((addr) => addr.id === selectedId);
    if (selectedAddress) {
      onConfirm(selectedAddress);
    }
  };

  const formatAddressLine1 = (address: Address) => address.street;
  const formatAddressLine2 = (address: Address) =>
    `${address.city}, ${address.state} ${address.zipCode}`;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      className="shipping-address-modal"
      width={420}
    >
      <div className="shipping-modal-content">
        {/* Close Button */}
        <button className="shipping-modal-close" onClick={onClose}>
          <CloseOutlined />
        </button>

        {/* Icon with ripple effect */}
        <div className="shipping-modal-icon-wrapper">
          <div className="shipping-modal-ripple shipping-modal-ripple-3" />
          <div className="shipping-modal-ripple shipping-modal-ripple-2" />
          <div className="shipping-modal-ripple shipping-modal-ripple-1" />
          <div className="shipping-modal-icon">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Back face */}
              <path
                d="M21 16V8C20.9996 7.6493 20.9071 7.30483 20.7315 7.00017C20.556 6.69552 20.3037 6.44132 20 6.26L13 2.26C12.696 2.08856 12.3511 1.99866 12 1.99866C11.6489 1.99866 11.304 2.08856 11 2.26L4 6.26C3.69626 6.44132 3.44398 6.69552 3.26846 7.00017C3.09294 7.30483 3.00036 7.6493 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9998C3.44398 17.3045 3.69626 17.5587 4 17.74L11 21.74C11.304 21.9114 11.6489 22.0013 12 22.0013C12.3511 22.0013 12.696 21.9114 13 21.74L20 17.74C20.3037 17.5587 20.556 17.3045 20.7315 16.9998C20.9071 16.6952 20.9996 16.3507 21 16Z"
                fill="#EDE7FB"
                stroke="#7E5AE2"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Top face */}
              <path
                d="M3.27002 6.96L12 12.01L20.73 6.96"
                stroke="#7E5AE2"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Center line */}
              <path
                d="M12 22.08V12"
                stroke="#7E5AE2"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Flap lines */}
              <path
                d="M7.5 4.6L16.5 9.6"
                stroke="#7E5AE2"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <Typography.Title level={4} className="shipping-modal-title">
          Shipping Address
        </Typography.Title>
        <Typography.Text className="shipping-modal-subtitle">
          Please choose the practice address
        </Typography.Text>

        {/* Address List */}
        <div className="shipping-modal-divider shipping-modal-divider-top" />
        <div className="shipping-modal-addresses">
          {addresses.map((address) => (
            <div
              key={address.id}
              className={`shipping-address-card ${selectedId === address.id ? "selected" : ""}`}
              onClick={() => setSelectedId(address.id)}
            >
              <div className="shipping-address-info">
                <Typography.Text className="shipping-address-label">
                  Address
                </Typography.Text>
                <div className="shipping-address-lines">
                  <Typography.Text className="shipping-address-value">
                    {formatAddressLine1(address)}
                  </Typography.Text>
                  <Typography.Text className="shipping-address-value shipping-address-line2">
                    {formatAddressLine2(address)}
                  </Typography.Text>
                </div>
              </div>
              <div className="shipping-address-checkbox">
                <div
                  className={`shipping-checkbox-custom ${selectedId === address.id ? "checked" : ""}`}
                >
                  {selectedId === address.id && (
                    <svg
                      width="12"
                      height="10"
                      viewBox="0 0 12 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 5L4.5 8.5L11 1.5"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="shipping-modal-divider shipping-modal-divider-bottom" />
        <div className="shipping-modal-actions">
          <Button onClick={onClose} className="shipping-cancel-btn">
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            disabled={!selectedId}
            className="shipping-confirm-btn"
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ShippingAddressModal;
